import type { ReactNode } from 'react';
import type { PayableInvoice } from '../../types/payments';
import { formatCurrency } from '../../lib/format';
import { formatPropertyLabel } from '../../lib/payments';
import type { RunSuccess } from '../../hooks/usePaymentRun';
import { Spinner } from '../Spinner';

interface RunProgressProps {
  /** The invoice currently being paid. */
  current: PayableInvoice;
  /** 1-based position within the run. */
  position: number;
  total: number;
  successes: RunSuccess[];
  /** Running total spent so far, per currency. */
  totals: Record<string, number>;
  /** True while the initiation request for the current invoice is in flight. */
  initiating?: boolean;
  onStop: () => void;
  /** The 2FA wait panel for the current invoice, rendered inside the progress context. */
  children?: ReactNode;
}

/**
 * Live progress for the "pay all pending" run (CHO-762): which invoice is
 * being paid, its position in the run, everything paid so far, and the running
 * per-currency total. Stop is always available, including mid-wait — stopping
 * never recalls an attempt that already reached the service.
 */
export function RunProgress({
  current,
  position,
  total,
  successes,
  totals,
  initiating = false,
  onStop,
  children,
}: RunProgressProps) {
  const remaining = total - position;
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Pay all pending — run in progress</h2>
          <p className="text-sm text-gray-500 mt-1">
            Invoice {position} of {total}
            {remaining > 0 ? ` — ${remaining} left after this one` : ' — the last one in the run'}. Each payment still
            needs its own approval on your phone.
          </p>
        </div>
        <button
          type="button"
          onClick={onStop}
          className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 shrink-0"
        >
          Stop run
        </button>
      </div>

      <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 px-4 py-3">
        <div className="flex justify-between gap-4 text-sm">
          <span className="text-gray-500">Now paying</span>
          <span className="font-medium text-gray-900 text-right">
            {formatPropertyLabel(current)} — {current.biller ?? '—'}
          </span>
        </div>
        <div className="flex justify-between gap-4 text-sm mt-1">
          <span className="text-gray-500">Amount</span>
          <span className="font-semibold text-gray-900">{formatCurrency(current.amount, current.currency)}</span>
        </div>
        {initiating && (
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
            <Spinner size={16} /> Initiating payment…
          </div>
        )}
      </div>

      {children}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">Paid so far ({successes.length})</h3>
          {successes.length === 0 ? (
            <p className="text-sm text-gray-400 mt-2">Nothing paid yet.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              {successes.map((success) => (
                <li key={success.attemptId} className="flex justify-between gap-3">
                  <span>
                    {formatPropertyLabel(success.invoice)} — {success.invoice.biller ?? '—'}
                  </span>
                  <span className="whitespace-nowrap">{formatCurrency(success.amount, success.currency)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">Running total</h3>
          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            {Object.entries(totals).map(([currency, amount]) => (
              <li key={currency} className="flex justify-between gap-3">
                <span className="text-gray-500">{currency}</span>
                <span className="font-medium text-gray-900">{formatCurrency(amount, currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
